import { useState } from 'react'
import { cn } from '@/lib/cn'
import { Badge } from '@/components/ui/Badge'
import { formatRelativeTime } from '@/lib/formatTime'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import type { HL7MessageLog } from '@/pages/MonitoringPage'

interface MessageRowProps {
  message: HL7MessageLog
}

const ADT_EVENT_LABELS: Record<string, string> = {
  A01: 'Admission',
  A02: 'Transfert',
  A03: 'Sortie',
  A04: 'Inscription',
  A05: 'Pré-admission',
  A08: 'Mise à jour',
  A11: 'Annulation admission',
  A12: 'Annulation transfert',
  A13: 'Annulation sortie',
}

const STATUS_CONFIG: Record<string, { dot: string; label: string; badge: 'success' | 'error' | 'warning' | 'default' }> = {
  COMPLETED: { dot: 'bg-success-500', label: 'Terminé', badge: 'success' },
  FAILED: { dot: 'bg-error-500', label: 'Échec', badge: 'error' },
  PROCESSING: { dot: 'bg-warning-500', label: 'En cours', badge: 'warning' },
  RECEIVED: { dot: 'bg-neutral-400', label: 'Reçu', badge: 'default' },
}

function getStatusConfig(status: string) {
  return STATUS_CONFIG[status] ?? { dot: 'bg-neutral-300', label: status, badge: 'default' as const }
}

export function MessageRow({ message }: MessageRowProps) {
  const [expanded, setExpanded] = useState(false)
  const statusConfig = getStatusConfig(message.processingStatus)
  const eventLabel = ADT_EVENT_LABELS[message.triggerEvent] ?? message.triggerEvent

  return (
    <div className="border-b border-neutral-100 last:border-b-0">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 transition-colors duration-150 text-left"
      >
        {/* Expand icon */}
        <div className="shrink-0 text-neutral-400">
          {expanded
            ? <ChevronDownIcon className="w-4 h-4" />
            : <ChevronRightIcon className="w-4 h-4" />
          }
        </div>

        {/* Status dot */}
        <div className={cn('w-2.5 h-2.5 rounded-full shrink-0', statusConfig.dot)} />

        {/* Message type badge */}
        <Badge variant="info" size="sm" className="shrink-0 font-mono">
          {message.messageType}^{message.triggerEvent}
        </Badge>

        {/* Sending facility */}
        <span className="text-sm font-medium text-neutral-800 truncate min-w-0 flex-1">
          {message.sendingFacility}
        </span>

        {/* Message control ID */}
        <span className="text-xs font-mono text-neutral-400 hidden sm:block truncate max-w-[120px]">
          {message.messageControlId}
        </span>

        {/* Relative time */}
        <span className="text-xs text-neutral-400 shrink-0 whitespace-nowrap">
          {formatRelativeTime(message.receivedAt)}
        </span>
      </button>

      {/* Expanded detail panel */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 ml-9 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <DetailItem label="Statut">
              <Badge variant={statusConfig.badge} size="sm">{statusConfig.label}</Badge>
            </DetailItem>
            <DetailItem label="Résultat">
              <span className={cn(
                'text-sm font-medium',
                message.processingOutcome === 'SUCCESS' && 'text-success-700',
                message.processingOutcome === 'PARSE_ERROR' && 'text-error-700',
                message.processingOutcome === 'SYSTEM_ERROR' && 'text-error-700',
              )}>
                {message.processingOutcome}
              </span>
            </DetailItem>
            <DetailItem label="Événement">
              <span className="text-sm text-neutral-700">{eventLabel}</span>
            </DetailItem>
            <DetailItem label="Établissement">
              <span className="text-sm text-neutral-700">{message.sendingFacility}</span>
            </DetailItem>
            <DetailItem label="ID Contrôle">
              <span className="text-sm font-mono text-neutral-700">{message.messageControlId}</span>
            </DetailItem>
            <DetailItem label="Tenant">
              <span className="text-sm text-neutral-700">{message.tenantId}</span>
            </DetailItem>
            <DetailItem label="Reçu le">
              <span className="text-sm text-neutral-700">
                {new Date(typeof message.receivedAt === 'number' && message.receivedAt < 1e12 ? message.receivedAt * 1000 : message.receivedAt).toLocaleString('fr-FR')}
              </span>
            </DetailItem>
            <DetailItem label="Hash SHA-256">
              <span className="text-xs font-mono text-neutral-400 break-all">{message.rawMessageHash}</span>
            </DetailItem>
          </div>

          {message.errorMessage && (
            <div className="rounded-md bg-error-50 border border-error-300 px-3 py-2">
              <p className="text-xs font-medium text-error-700 mb-0.5">Erreur</p>
              <p className="text-sm text-error-600 font-mono whitespace-pre-wrap">{message.errorMessage}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function DetailItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-neutral-400 mb-0.5">{label}</p>
      {children}
    </div>
  )
}
